import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
    name: "", slug: "", description: "", category: "apparel",
    price_cents: 0, image_url: "", sizes: [], in_stock: true, is_published: true, sort_order: 0,
};

export function AdminProducts() {
    const [items, setItems] = useState([]);
    const [edit, setEdit] = useState(null);
    const load = () => api.get("/products").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        const payload = {
            ...edit,
            price_cents: parseInt(edit.price_cents, 10) || 0,
            sort_order: parseInt(edit.sort_order, 10) || 0,
            sizes: typeof edit.sizes === "string"
                ? edit.sizes.split(",").map((s) => s.trim()).filter(Boolean)
                : (edit.sizes || []),
        };
        try {
            if (edit.id) await api.put(`/products/${edit.id}`, payload);
            else await api.post("/products", payload);
            toast.success("Saved");
            setEdit(null); load();
        } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    };

    const del = async (p) => {
        if (!confirm(`Delete ${p.name}?`)) return;
        await api.delete(`/products/${p.id}`);
        toast.success("Deleted"); load();
    };

    return (
        <div data-testid="admin-products">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-2">Store</div>
                    <h1 className="h-display text-4xl">Products</h1>
                </div>
                <button type="button" onClick={() => setEdit({ ...EMPTY })} data-testid="add-product" className="btn-gold px-4 py-2 text-xs">+ Add Product</button>
            </div>

            <div className="space-y-3">
                {items.map((p) => (
                    <div key={p.id} className="card-flat p-4 flex items-center gap-4" data-testid={`prod-${p.id}`}>
                        <div className="w-14 h-14 bg-bg-soft bg-cover bg-center shrink-0" style={{ backgroundImage: `url('${p.image_url}')` }} />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold truncate">{p.name}</div>
                            <div className="text-xs text-ink mt-1">{p.category} · ${(p.price_cents/100).toFixed(2)} · sort {p.sort_order}{p.is_published ? "" : " · UNPUBLISHED"}</div>
                        </div>
                        <button type="button" onClick={() => setEdit({ ...p, sizes: (p.sizes || []).join(", ") })} className="text-gold text-xs uppercase tracking-widest hover:text-gold-bright">Edit</button>
                        <button type="button" onClick={() => del(p)} className="text-red-400 text-xs uppercase tracking-widest hover:text-red-300">Delete</button>
                    </div>
                ))}
                {items.length === 0 && <div className="card-flat p-12 text-center text-ink">No products.</div>}
            </div>

            {edit && (
                <div className="fixed inset-0 bg-black/80 grid place-items-center z-50 p-5" onClick={(e) => e.target === e.currentTarget && setEdit(null)}>
                    <form onSubmit={save} className="bg-bg-soft border border-gold/40 max-w-2xl w-full p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="product-edit-modal">
                        <h2 className="h-display text-2xl mb-2">{edit.id ? "Edit Product" : "New Product"}</h2>
                        <F label="Name" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} />
                        <F label="Slug" value={edit.slug} onChange={(v) => setEdit({ ...edit, slug: v })} />
                        <div className="grid grid-cols-2 gap-3">
                            <F label="Category (apparel | footwear | accessories)" value={edit.category} onChange={(v) => setEdit({ ...edit, category: v })} />
                            <F label="Price (cents, e.g. 8999 = $89.99)" type="number" value={edit.price_cents} onChange={(v) => setEdit({ ...edit, price_cents: v })} />
                        </div>
                        <F label="Image URL" value={edit.image_url} onChange={(v) => setEdit({ ...edit, image_url: v })} />
                        <F label="Sizes (comma-separated, leave blank if none)" value={edit.sizes} onChange={(v) => setEdit({ ...edit, sizes: v })} />
                        <div>
                            <label className="block text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">Description</label>
                            <textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={4}
                                className="w-full px-3 py-2 text-sm bg-bg border border-line focus:border-gold outline-none" />
                        </div>
                        <div className="grid grid-cols-3 gap-3 items-center">
                            <F label="Sort Order" type="number" value={edit.sort_order} onChange={(v) => setEdit({ ...edit, sort_order: v })} />
                            <label className="text-sm flex items-center gap-2 mt-6"><input type="checkbox" checked={edit.in_stock} onChange={(e) => setEdit({ ...edit, in_stock: e.target.checked })} /> In stock</label>
                            <label className="text-sm flex items-center gap-2 mt-6"><input type="checkbox" checked={edit.is_published} onChange={(e) => setEdit({ ...edit, is_published: e.target.checked })} /> Published</label>
                        </div>
                        <div className="flex justify-end gap-2 pt-3">
                            <button type="button" onClick={() => setEdit(null)} className="px-4 py-2 text-xs text-ink hover:text-white">Cancel</button>
                            <button type="submit" className="btn-gold px-5 py-2 text-xs">Save</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function F({ label, value, onChange, type = "text" }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">{label}</label>
            <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 text-sm bg-bg border border-line focus:border-gold outline-none" />
        </div>
    );
}

export function AdminStoreOrders() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/store/orders").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const updateOrder = async (oid, status, tracking) => {
        const params = new URLSearchParams({ status });
        if (tracking) params.append("tracking_number", tracking);
        await api.put(`/store/orders/${oid}/fulfillment?${params.toString()}`);
        toast.success("Updated"); load();
    };

    return (
        <div data-testid="admin-store-orders">
            <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-2">Store</div>
            <h1 className="h-display text-4xl mb-8">Orders</h1>
            <div className="space-y-3">
                {items.map((o) => (
                    <div key={o.id} className="card-flat p-5" data-testid={`order-${o.id}`}>
                        <div className="flex flex-wrap justify-between gap-3 text-xs">
                            <span className="font-mono text-gold">{o.id.slice(0, 8).toUpperCase()}</span>
                            <span className="text-ink">{new Date(o.created_at).toLocaleString()}</span>
                            <span className={`uppercase tracking-widest font-bold ${o.payment_status === "paid" ? "text-green-400" : "text-amber-400"}`}>{o.payment_status}</span>
                            <span className="uppercase tracking-widest font-bold text-purple-light">{o.fulfillment_status}</span>
                        </div>
                        <div className="mt-3 text-sm">
                            <div className="font-bold">{o.buyer_name} · {o.buyer_email}</div>
                            <div className="text-ink text-xs mt-1">
                                {o.shipping_address.line1}{o.shipping_address.line2 ? `, ${o.shipping_address.line2}` : ""}, {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.postal_code}
                            </div>
                        </div>
                        <div className="mt-3 space-y-1">
                            {o.items.map((i, idx) => (
                                <div key={idx} className="text-sm flex justify-between">
                                    <span>{i.product_name}{i.size ? ` · ${i.size}` : ""} × {i.quantity}</span>
                                    <span className="font-bold">${(i.unit_price_cents * i.quantity / 100).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="border-t border-line mt-3 pt-3 flex justify-between text-sm">
                            <span>Total</span><span className="font-bold text-gold">${(o.total_cents / 100).toFixed(2)}</span>
                        </div>
                        {o.payment_status === "paid" && (
                            <div className="mt-4 flex flex-wrap gap-2 items-center">
                                <FulfillBtn current={o.fulfillment_status} val="pending" onClick={() => updateOrder(o.id, "pending")} label="Pending" />
                                <FulfillBtn current={o.fulfillment_status} val="shipped" onClick={() => {
                                    const t = prompt("Tracking number?", o.tracking_number || "");
                                    if (t !== null) updateOrder(o.id, "shipped", t);
                                }} label="Mark Shipped" />
                                <FulfillBtn current={o.fulfillment_status} val="delivered" onClick={() => updateOrder(o.id, "delivered")} label="Delivered" />
                                <FulfillBtn current={o.fulfillment_status} val="cancelled" onClick={() => updateOrder(o.id, "cancelled")} label="Cancel" />
                                {o.tracking_number && <span className="text-xs text-ink ml-2">Tracking: <span className="text-white font-mono">{o.tracking_number}</span></span>}
                            </div>
                        )}
                    </div>
                ))}
                {items.length === 0 && <div className="card-flat p-12 text-center text-ink">No orders yet.</div>}
            </div>
        </div>
    );
}

function FulfillBtn({ current, val, onClick, label }) {
    const active = current === val;
    return (
        <button type="button" onClick={onClick}
            className={`px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-bold border transition-colors ${active ? "border-gold bg-gold text-bg" : "border-line text-ink hover:border-gold hover:text-gold"}`}>
            {label}
        </button>
    );
}
